import axios, { AxiosError, AxiosResponse } from "axios";
import axiosConfig from "./axiosConfig";
import logHttpRequest from "../logging/logHttpRequest";
import EnhancedAxiosError from "./EnhancedAxiosError";

const RETRY_COUNT = 0;
const WAIT_INTERVAL = 500;

const ABORTED_CODE = "ECONNABORTED";
const RETRY_ERROR_CODES = new Set<string>([
  "ETIMEDOUT",
  "ECONNRESET",
  "EADDRINUSE",
  "ESOCKETTIMEDOUT",
  "ECONNREFUSED",
  "EPIPE",
]);

const IDEMPOTENT_HTTP_METHODS = ["DELETE", "PUT"];
const SAFE_HTTP_METHODS = new Set<string>([
  "GET",
  "HEAD",
  "OPTIONS",
  ...IDEMPOTENT_HTTP_METHODS,
]);

const DEFAULT_SAFE_STATUS_CODE_RANGES = [
  [100, 199],
  [429, 429],
  [500, 599],
];

/**
 * Simple async exponential backoff.
 *
 * @param {number} attemptNumber
 * @returns {Promise<void>}
 */
function waitForBackoff(attemptNumber: number): Promise<void> {
  // Exponentially increase wait time.
  const waitTime = WAIT_INTERVAL * 2 ** attemptNumber;
  return new Promise((resolve) => {
    console.log(`Waiting ${waitTime}ms`);
    setTimeout(() => {
      resolve();
    }, waitTime);
  });
}

/**
 * Determines if response status code indicates retryable.
 *
 * @param {number} code
 * @returns {boolean}
 */
function isRetryableStatusCode(code: number): boolean {
  return DEFAULT_SAFE_STATUS_CODE_RANGES.some((range) => {
    return code >= range[0] && code <= range[1];
  });
}

/**
 * Determines if request method is retryable.
 *
 * @param {AxiosError} error
 * @returns {boolean}
 */
function isRetryableRequestMethod(error: AxiosError): boolean {
  if (!error.config || !error.config.method) {
    return false;
  }

  return SAFE_HTTP_METHODS.has(error.config.method.toUpperCase());
}

/**
 * Determines if request can be retried based on method and status.
 *
 * @param {AxiosError} error
 * @returns {boolean}
 */
function isRetryableRequest(error: AxiosError): boolean {
  if (!error.response) {
    return false;
  }

  return (
    isRetryableRequestMethod(error) &&
    isRetryableStatusCode(error.response.status)
  );
}

/**
 * Determines if error indicates a network error.
 *
 * @param {AxiosError} error
 * @returns {boolean}
 */
function isNetworkError(error: AxiosError): boolean {
  // CORS ERROR likely.
  if (error.config && !error.response && !error.code) {
    return true;
  }

  return (
    !error.response &&
    Boolean(error.code) &&
    error.code !== ABORTED_CODE &&
    RETRY_ERROR_CODES.has((error.code || "").toUpperCase())
  );
}

/**
 * Determines if axios request should be retried.
 *
 * @param {AxiosError} error
 * @returns {boolean}
 */
function shouldRetry(error: EnhancedAxiosError): boolean {
  return (
    (error.config && error.config.forceRetry) ||
    isNetworkError(error) ||
    isRetryableRequest(error)
  );
}

/**
 * Retries requests using a new axios instance.
 *
 * @param {AxiosError} inputError
 * @returns {Promise<AxiosResponse<unknown>>}
 */
async function retryRequest(
  inputError: EnhancedAxiosError
): Promise<AxiosResponse<unknown>> {
  const axiosInstance = axios.create(axiosConfig);

  for (let i = 0; i < RETRY_COUNT; i++) {
    try {
      // disable eslint becaue awaiting in loop by design.
      // eslint-disable-next-line no-await-in-loop
      await waitForBackoff(i);

      console.log(`Retry #${i + 1}`);
      logHttpRequest(inputError.config);

      // disable eslint becaue awaiting in loop by design.
      // eslint-disable-next-line no-await-in-loop
      return await axiosInstance(inputError.config);
    } catch (error) {
      const axiosError = error as EnhancedAxiosError;
      if (!shouldRetry(axiosError)) {
        return Promise.reject(error);
      }
    }
  }

  return Promise.reject(inputError);
}

/**
 * Error handler for http retry interceptor.
 *
 * @export
 * @param {unknown} error
 * @returns {Promise<AxiosResponse<unknown>>}
 */
export default async function httpRetryInterceptorErrorHandler(
  error: unknown
): Promise<AxiosResponse<unknown>> {
  const axiosError = error as EnhancedAxiosError;
  if (!axiosError || !shouldRetry(axiosError)) {
    console.log("Cannot retry request.");
    return Promise.reject(error);
  }

  return retryRequest(axiosError);
}
